/**
 * Maps a client's applied `Flag` resources onto the rows the info box and the popover render.
 *
 * An active flag stores no copy of its library entry's name, category or priority — only a
 * reference to it. Everything a row displays is resolved from the entry at read time, so a rename
 * in the library reaches every chart carrying the flag without a migration. Removing a flag freezes
 * those values onto it — see `buildRemovedClientFlagResource`.
 */

import { getExtension, resolveId, WithId } from "@medplum/core";
import type { Extension, Flag, Reference } from "@medplum/fhirtypes";

import { CLIENT_FLAG_IDENTIFIER_SYSTEM } from "@/fhir/constants/identifier";
import { Extensions } from "@/fhir/enums/extensions";
import { QuiltedHealthCodeSystems } from "@/fhir/enums/quilted-health";
import { joinReferenceString } from "@/utils/medplum";

import {
  INFO_BOX_VISIBLE_FLAG_COUNT,
  OVERFLOW_COUNT_SEPARATOR,
  OVERFLOW_TERM_SEPARATOR,
} from "../constants/client-flags";
import { ACTIVE_FLAG_STATUS, FLAG_PRIORITY_CODINGS, FlagPriority } from "../constants/flags";
import { ClientFlag, ClientFlagsSummary } from "../interfaces/client-flags";
import { PracticeFlagEntry } from "../interfaces/flags";

import { buildActiveFlagQuery } from "./flag-query";
import { getFlagPriorityRank } from "./flags";
import { getLibraryFlagCode, toPracticeFlag } from "./practice-flag";

/**
 * The library entry an applied flag points at.
 *
 * @param flag - An applied `Flag` from a client's chart
 * @returns The entry's logical id, or `undefined` when the extension is absent or unreferenceable
 */
export function getLibraryFlagId(flag: Flag): string | undefined {
  const reference = getExtension(flag, Extensions.ClientFlagLibraryEntry)?.valueReference as
    | Reference<Flag>
    | undefined;

  return reference ? resolveId(reference) : undefined;
}

/**
 * Builds the row for one applied flag.
 *
 * @param flag - The applied `Flag`, as searched from the chart
 * @param libraryEntry - Its library entry, as `toPracticeFlag` produced it
 * @param isDeprecated - Whether that entry has been deleted from the library
 */
export function toClientFlag(
  flag: WithId<Flag>,
  libraryEntry: PracticeFlagEntry,
  isDeprecated: boolean,
): ClientFlag {
  return {
    id: flag.id,
    libraryFlagId: libraryEntry.id,
    name: libraryEntry.name,
    category: libraryEntry.category,
    priority: libraryEntry.priority,
    isDeprecated,
  };
}

/**
 * Orders rows High → Medium → Low, then by name so a tie is stable between reads. Chart rows and
 * library entries share this order, so the filter drawer lists entries the way a chart shows them.
 *
 * A flag the practice saved without a priority sorts last — nothing ranks it, so it ranks below
 * everything that is ranked.
 */
export function sortClientFlagsByPriority<TFlag extends Pick<ClientFlag, "name" | "priority">>(
  flags: TFlag[],
): TFlag[] {
  return [...flags].sort(
    (a, b) =>
      getFlagPriorityRank(a.priority) - getFlagPriorityRank(b.priority) ||
      a.name.localeCompare(b.name),
  );
}

/**
 * Splits ordered rows into the two the info box shows and a breakdown of the rest.
 *
 * The breakdown counts only the hidden flags, and only those whose priority is still shown — the
 * overflow line reads "1 Medium, 1 Low", so a flag with no priority, or a deprecated one whose
 * priority is replaced by the `Deprecated` chip, contributes to `overflowCount` without earning a
 * term of its own.
 *
 * @param flags - Rows already ordered by `sortClientFlagsByPriority`
 */
export function summarizeClientFlags(flags: ClientFlag[]): ClientFlagsSummary {
  const visible = flags.slice(0, INFO_BOX_VISIBLE_FLAG_COUNT);
  const hidden = flags.slice(INFO_BOX_VISIBLE_FLAG_COUNT);

  const countsByPriority = hidden.reduce<Partial<Record<FlagPriority, number>>>((counts, flag) => {
    if (!flag.priority || flag.isDeprecated) return counts;

    return { ...counts, [flag.priority]: (counts[flag.priority] ?? 0) + 1 };
  }, {});

  const overflowByPriority = Object.values(FlagPriority).reduce<
    ClientFlagsSummary["overflowByPriority"]
  >((terms, priority) => {
    const count = countsByPriority[priority];

    return count ? [...terms, { priority, count }] : terms;
  }, []);

  return { visible, overflowCount: hidden.length, overflowByPriority };
}

/**
 * Writes the info box's overflow line — `+2 more · 1 Medium, 1 Low`.
 *
 * @param overflowCount - Hidden flags in total, as `summarizeClientFlags` counted them
 * @param terms - The hidden flags that carry a priority, highest first
 */
export function buildOverflowLabel(
  overflowCount: number,
  terms: ClientFlagsSummary["overflowByPriority"],
): string {
  const summary = terms
    .map(({ priority, count }) => `${count} ${FLAG_PRIORITY_CODINGS[priority].display}`)
    .join(OVERFLOW_TERM_SEPARATOR);

  // Hidden flags with no priority leave nothing to name, so the line stops at the count.
  return summary
    ? `+${overflowCount} more${OVERFLOW_COUNT_SEPARATOR}${summary}`
    : `+${overflowCount} more`;
}

/**
 * Builds the `Flag` that applies one library entry to a chart.
 *
 * Nothing the library owns is copied: `code` carries the entry's coding with no `display`, because
 * R4 requires `Flag.code` and readers take the name from the entry the extension points at. A
 * rename leaving that coding stale therefore changes nothing on screen.
 *
 * @param libraryFlag - The library entry being applied, as stored
 * @param clientId - Patient logical id the flag is going onto
 * @param appliedAt - ISO timestamp the flag is being applied at, passed in rather than read here so
 *   this stays pure and a test pins an exact value
 */
export function buildClientFlagResource(
  libraryFlag: WithId<Flag>,
  clientId: string,
  appliedAt: string,
): Flag {
  return {
    resourceType: "Flag",
    status: ACTIVE_FLAG_STATUS,
    subject: { reference: joinReferenceString("Patient", clientId) },
    // R4 defines `period` as activation to inactivation. Opened here and closed by a removal, so
    // the archive reads when a flag stopped applying from the resource rather than from history.
    period: { start: appliedAt },
    identifier: [{ system: CLIENT_FLAG_IDENTIFIER_SYSTEM, value: libraryFlag.id }],
    code: {
      coding: [
        {
          system: QuiltedHealthCodeSystems.LibraryFlag,
          code: getLibraryFlagCode(libraryFlag) ?? libraryFlag.id,
        },
      ],
    },
    extension: [
      {
        url: Extensions.ClientFlagLibraryEntry,
        valueReference: { reference: joinReferenceString("Flag", libraryFlag.id) },
      },
    ],
  };
}

/**
 * Builds the `Flag` that replaces an applied one when a provider removes it from the chart.
 *
 * The resource is kept and deactivated rather than deleted, so the chart holds a record of what was
 * once flagged. Three things are recorded alongside the status, because the archive shows all
 * three and none of them can be recovered afterwards: when it was removed (`period.end`, R4's own
 * element for it), who removed it, and why.
 *
 * `removedBy` and `reason` ride on extensions because `Flag` has no element for either —
 * `Flag.author` is the flag's *creator*, which is a different fact and must not be overwritten.
 *
 * The flag is also frozen as the chart showed it at that moment, because the archive is a fixed
 * record and must not follow later library edits. The library entry's name goes on the library
 * coding's `display` and its category and priority codings onto `category[]` — the same shape a
 * library entry has, so `toPracticeFlag` reads it back. A library entry that was already deleted
 * adds the `ClientFlagLibraryEntryDeleted` marker, so the archive keeps the `Deprecated` chip.
 *
 * Every other extension is carried across, so the library link survives a removal.
 *
 * @param flag - The applied flag as stored, which keeps its id and `meta`
 * @param libraryFlag - The library entry the flag points at, as read at the moment of removal
 * @param removedAt - ISO timestamp of the removal
 * @param removedBy - `Practitioner/{id}` reference to whoever removed it
 * @param reason - The clinician's reason — a listed one, or their own words for `Other` — or
 *   `undefined` when none was given. PHI.
 */
export function buildRemovedClientFlagResource(
  flag: WithId<Flag>,
  libraryFlag: WithId<Flag>,
  { removedAt, removedBy, reason }: { removedAt: string; removedBy: string; reason?: string },
): WithId<Flag> {
  const isLibraryEntryDeleted = libraryFlag.status !== ACTIVE_FLAG_STATUS;
  const removalExtensions: Extension[] = [
    { url: Extensions.ClientFlagRemovedBy, valueReference: { reference: removedBy } },
    ...(reason ? [{ url: Extensions.ClientFlagRemovalReason, valueString: reason }] : []),
    ...(isLibraryEntryDeleted
      ? [{ url: Extensions.ClientFlagLibraryEntryDeleted, valueBoolean: true }]
      : []),
  ];

  // Re-removal would otherwise stack a second set; the earlier one is replaced, not appended.
  const replacedUrls: string[] = [
    Extensions.ClientFlagRemovedBy,
    Extensions.ClientFlagRemovalReason,
    Extensions.ClientFlagLibraryEntryDeleted,
  ];

  return {
    ...flag,
    status: "inactive",
    period: { ...flag.period, end: removedAt },
    code: {
      ...flag.code,
      coding: [
        ...(flag.code?.coding ?? []).filter(
          ({ system }) => system !== QuiltedHealthCodeSystems.LibraryFlag,
        ),
        {
          system: QuiltedHealthCodeSystems.LibraryFlag,
          code: getLibraryFlagCode(libraryFlag) ?? libraryFlag.id,
          display: toPracticeFlag(libraryFlag).name,
        },
      ],
    },
    category: libraryFlag.category,
    extension: [
      ...(flag.extension ?? []).filter(({ url }) => !replacedUrls.includes(url)),
      ...removalExtensions,
    ],
  };
}

/**
 * The query that pins one library entry to one active flag on one chart, so a double-submit or a
 * stale drawer cannot apply the same flag twice. See `buildActiveFlagQuery` for the shape.
 */
export function buildClientFlagGuardQuery(libraryFlagId: string, clientId: string): string {
  return buildActiveFlagQuery({
    system: CLIENT_FLAG_IDENTIFIER_SYSTEM,
    value: libraryFlagId,
    subject: joinReferenceString("Patient", clientId),
  });
}
